import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Brain, FileText, Download, Home, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CounselingReportsPage = ({ user }) => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/counseling/my-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data.reports);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const viewFullReport = async (reportId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/counseling/report/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedReport(response.data);
    } catch (error) {
      toast.error('Failed to load report details');
    }
  };

  const downloadPDF = (report) => {
    // Download student report (not admin report)
    const reportText = report.student_report || report.full_report || '';
    const element = document.createElement('a');
    const file = new Blob([reportText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `Career_Report_${report.user_name.replace(' ', '_')}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('Student report downloaded');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-16 h-16 text-indigo-500 mx-auto mb-4 animate-pulse" />
          <p className="text-lg text-slate-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  if (selectedReport) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-teal-50">
        <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Brain className="w-8 h-8 text-indigo-500" />
              <h1 className="font-heading text-2xl font-bold text-slate-900">Career Report</h1>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setSelectedReport(null)} variant="outline">
                Back to Reports
              </Button>
              <Button onClick={() => downloadPDF(selectedReport)} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Download
              </Button>
            </div>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-6 py-12">
          <Card className="bg-white rounded-2xl border border-slate-200 shadow-lg">
            <CardContent className="p-8">
              <div className="prose max-w-none">
                <div className="mb-8 p-6 bg-gradient-to-r from-indigo-50 to-teal-50 rounded-xl border border-indigo-100">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                    {selectedReport.recommendation}
                  </h2>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <span>Track: {selectedReport.track === 'A' ? 'Stream Selection' : 'Career Mapping'}</span>
                    <span>•</span>
                    <span>Cognitive Score: {selectedReport.cognitive_score}%</span>
                    <span>•</span>
                    <span>{new Date(selectedReport.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                  {selectedReport.full_report}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-teal-50">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-indigo-500" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">My Career Reports</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/counseling')} variant="outline">
              Start New Session
            </Button>
            <Button onClick={() => navigate('/dashboard')} variant="ghost">
              <Home className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {reports.length === 0 ? (
          <Card className="bg-white rounded-2xl border border-slate-200 shadow-lg">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                No Reports Yet
              </h2>
              <p className="text-slate-600 mb-6">
                Start your first career counseling session to get personalized recommendations
              </p>
              <Button
                onClick={() => navigate('/counseling')}
                className="rounded-full px-8 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold"
              >
                Start Counseling
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {reports.map((report) => (
              <Card
                key={report.report_id}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => viewFullReport(report.report_id)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-heading text-xl mb-2">
                        {report.recommendation}
                      </CardTitle>
                      <p className="text-sm text-slate-600">
                        {report.track === 'A' ? 'Stream Selection Report' : 'Career Mapping Report'}
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      report.cognitive_score >= 80
                        ? 'bg-teal-100 text-teal-700'
                        : report.cognitive_score >= 60
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {report.cognitive_score}%
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Top Scores:</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(report.scores || {})
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 3)
                          .map(([key, value]) => (
                            <span
                              key={key}
                              className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs"
                            >
                              {key}: {value.toFixed(1)}
                            </span>
                          ))}
                      </div>
                    </div>

                    {report.gaps && report.gaps.length > 0 && (
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Areas to Improve:</p>
                        <p className="text-sm text-slate-700">
                          {report.gaps.slice(0, 2).join(', ')}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500">
                        {new Date(report.created_at).toLocaleDateString()}
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-indigo-600 hover:text-indigo-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadPDF(report);
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CounselingReportsPage;
